'use strict'
import { safeJsonParse } from './safeJson'

const storage = (type) => ({
  set(key, value) {
    window[type].setItem(key, JSON.stringify(value))
  },
  get(key, defaultValue) {
    const item = window[type].getItem(key)
    if (item === 'undefined') return defaultValue
    /* localStorage / sessionStorage 的内容可被用户或扩展随意改写，裸 JSON.parse
       抛出的 SyntaxError 会把调用方（含请求拦截器）整个打断，统一按「取不到值」处理 */
    return safeJsonParse(item, defaultValue) ?? defaultValue
  },
  remove(key) {
    window[type].removeItem(key)
  },
  clear() {
    window[type].clear()
  }
})

export const local = storage('localStorage')
export const session = storage('sessionStorage')

/** 分享 / 免登编辑场景的令牌在 localStorage 里的键名 */
export const EDIT_TOKEN_KEY = 'editToken'

/* 令牌的合法字符集：可见 ASCII（0x21-0x7e，不含空格）。
   它会被拼进 authorization 请求头，而请求头值不允许空白与控制字符——localStorage 被写入
   带 \r\n 或前后空格的值时，浏览器会以「非法头值」直接抛 TypeError，整个请求挂掉。
   超出这个字符集的内容本来就没法当头值用，一律按「没有令牌」处理。 */
const VALID_EDIT_TOKEN_RE = /^[\x21-\x7e]+$/

/**
 * 当前地址的 query 里 skipToken 是否为 'true'。
 * 路由是 hash 模式，query 在 # 之后；也兼容写在 # 之前的 ?skipToken=true。
 */
const isSkipTokenUrl = () => {
  try {
    const { search, hash } = window.location
    const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?')) : ''
    return [search, hashQuery].some((q) => new URLSearchParams(q).get('skipToken') === 'true')
  } catch (error) {
    return false
  }
}

/**
 * 读取分享 / 免登编辑场景的令牌。
 *
 * 只有链接 query 的 skipToken 不为 'true' 时才用 editToken：skipToken=true 表示从站内「编辑」
 * 按钮打开、用户已登录，此时 editToken 可能是之前外链编辑留下的过期令牌，改为返回登录令牌（TOKEN），
 * 调用方 `code ? getEditToken() : local.get('TOKEN')` 无需改动。
 *
 * 存进去的是 JSON.stringify 后的字符串，但 localStorage 是可被随意改写的，两层兜底：
 * 1. 内容被篡改或截断时 JSON.parse 会抛异常，直接在接口层裸调用会让整个请求函数挂掉；
 * 2. 解析出来的值还要满足请求头的字符要求，否则同样会在发请求时抛错。
 * 两层都不满足时按「没有令牌」处理，交由后端返回 401，而不是让前端崩在拼请求头这一步。
 *
 * @returns {string} 令牌；取不到或格式非法时返回空串
 */
export const getEditToken = () => {
  if (isSkipTokenUrl()) {
    const loginToken = local.get('TOKEN', '')
    return typeof loginToken === 'string' && VALID_EDIT_TOKEN_RE.test(loginToken) ? loginToken : ''
  }
  const raw = window.localStorage.getItem(EDIT_TOKEN_KEY)
  if (!raw) return ''
  let token = ''
  try {
    const parsed = JSON.parse(raw)
    token = typeof parsed === 'string' ? parsed : ''
  } catch (error) {
    // 兼容早期直接存裸字符串的数据；对象 / 数组的残缺 JSON 一并按空处理
    token = !raw.startsWith('{') && !raw.startsWith('[') ? raw : ''
  }
  token = token.trim()
  return VALID_EDIT_TOKEN_RE.test(token) ? token : ''
}
